import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, collection, addDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import AddCardIcon from '@mui/icons-material/AddCard';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import StoreIcon from '@mui/icons-material/Store';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import Header from '../components/Header';
import WelcomeScreen from './WelcomeScreen';

function Menu({ user, subscriptions, trialEnd }) {
  const [isInitialized, setIsInitialized] = useState(() => {
    // 初回マウント時に localStorage を見て即時描画できるようにする
    try {
      if (!user) return null;
      const cached = localStorage.getItem(`isInitialized_${user.uid}`);
      return cached !== null ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!user) return;

    const storageKey = `isInitialized_${user.uid}`;

    // 1) まずローカルキャッシュを使って即描画（あれば）
    const cached = localStorage.getItem(storageKey);
    if (cached !== null) {
      try {
        setIsInitialized(JSON.parse(cached));
      } catch {
        setIsInitialized(null);
      }
    } else {
      // キャッシュがなければ読み込み中表示
      setIsInitialized(null);
    }

    // 2) その後に Firestore で最新を取得して上書き
    const fetchIsInitialized = async () => {
      const userRef = doc(db, 'customers', user.uid);
      const docSnap = await getDoc(userRef);
      if (docSnap.exists()) {
        const value = docSnap.data().isInitialized ?? false;
        setIsInitialized(value);
        localStorage.setItem(storageKey, JSON.stringify(value));
      } else {
        setIsInitialized(false);
        localStorage.setItem(storageKey, 'false');
      }
    };

    fetchIsInitialized();
  }, [user]);

  const handleComplete = async () => {
    if (!user) return;
    const storageKey = `isInitialized_${user.uid}`;
    const userRef = doc(db, 'customers', user.uid);
    await setDoc(userRef, { isInitialized: true }, { merge: true });
    setIsInitialized(true);
    localStorage.setItem(storageKey, 'true');
  };

  // ローディング中（キャッシュが無い初回のみ基本的に表示される）
  if (isInitialized === null) {
    return <div>Loading...</div>;
  }

  // 初回 → WelcomeScreen を表示
  if (!isInitialized) {
    return <WelcomeScreen onComplete={handleComplete} />;
  }

  // 通常の Menu を表示
  return (
    <div className="Menu" style={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Header text={'メニュー'}/>
      <Box sx={{ width: '100%', mt: 5 }}>
        <Stack spacing={2} alignItems="center">
          <MenuButton startIcon={<StoreIcon />} label='店舗情報管理' link='/shopInfo'/>
          <MenuButton startIcon={<BookOnlineIcon />} label='クーポン管理' link='/couponList'/>
          <MenuButton startIcon={<AddCardIcon />} label='ポイントカード発行' link='/cardIssuance'/>
          <MenuButton startIcon={<LocalParkingIcon />} label='ポイント付与/使用' link='/pointReading'/>
        </Stack>
        {/* 決済情報登録ボタン（サブスクリプション未登録時のみ表示） */}
        {!subscriptions && (
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ width: '70%', fontWeight: 'bold', fontSize: '1.1rem', mb: 1 }}
              onClick={() => handleCheckout(user, trialEnd)}
            >
              決済情報を登録する
            </Button>
            <div style={{ fontSize: '0.9rem', color: '#666', lineHeight: 1.6 }}>
              {trialEnd ? (() => {
                const now = new Date();
                const diffTime = trialEnd.getTime() - now.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                if (diffDays > 7) {
                  return (
                    <>
                      無料トライアル残り<strong>{diffDays}日</strong>です。<br />
                      トライアル終了後もご利用いただくには、<br />
                      決済情報のご登録が必要です。<br />
                      <span style={{ fontSize: '0.85rem', color: '#999' }}>
                        ※決済情報を登録した後も、<br />
                        トライアル終了まで料金は発生しません
                      </span>
                    </>
                  );
                } else if (diffDays > 0) {
                  return (
                    <span style={{ color: '#d32f2f' }}>
                      無料トライアル残り<strong>{diffDays}日</strong>です。<br />
                      トライアル終了後もご利用いただくには、<br />
                      決済情報のご登録が必要です。<br />
                      <span style={{ fontSize: '0.85rem', color: '#999' }}>
                        ※決済情報を登録した後も、<br />
                        トライアル終了まで料金は発生しません
                      </span>
                    </span>
                  );
                } else {
                  return (
                    <span style={{ color: '#d32f2f' }}>
                      無料トライアルは終了しました。<br />
                      ご利用いただくには、決済情報のご登録が必要です。
                    </span>
                  );
                }
              })() : (
                '有料プランのご利用には決済情報の登録が必要です'
              )}
            </div>
          </Box>
        )}
      </Box>
    </div>
  );

// 決済情報登録ボタンのクリック時処理（firestore-stripe-payments拡張方式）
async function handleCheckout(user, trialEnd) {
  if (!user) return;
  try {
    const selectedPrice = {
      // プランが1つのため、固定値で入れている
      price: process.env.REACT_APP_STRIPE_PRICE_ID,
      quantity: 1,
    };

    // 残りトライアル日数を計算（終了済みなら0）
    let trialDays = 0;
    if (trialEnd) {
      const now = new Date();
      const diffTime = trialEnd.getTime() - now.getTime();
      trialDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
    }

    const checkoutSession = {
      automatic_tax: true,
      cancel_url: window.location.origin,
      collect_shipping_address: true,
      line_items: [selectedPrice],
      metadata: {
        key: 'value',
      },
      mode: 'subscription',
      success_url: window.location.origin,
      tax_id_collection: true,
      // 残りトライアル日数があれば設定
      ...(trialDays > 0 && { trial_period_days: trialDays }),
      subscription_data: {
        items: [
          {
            price: process.env.REACT_APP_STRIPE_PRICE_ID
          }
        ],
      },
    };

    const docRef = await addDoc(collection(db, 'customers', user.uid, 'checkout_sessions'), checkoutSession);
    onSnapshot(docRef, (snap) => {
      const { error, url } = snap.data() || {};
      if (error) {
        alert(`エラーが発生しました: ${error.message}`);
      }
      if (url) {
        window.location.assign(url);
      }
    });
  } catch (e) {
    alert('決済ページの生成に失敗しました');
  }
}
}
export default Menu;

// 関数内の共通コンポーネント
const MenuButton = ({ startIcon, label, link }) => {
  return (
    <Button
      variant="contained"
      startIcon={startIcon}
      component={Link}
      to={link}
      sx={{
        width: '60%',
        height: 72,
        justifyContent: 'flex-start',
        fontWeight: 'bold',
        fontSize: '1rem',
        backgroundColor: '#fff',
        color: '#333',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        '&:hover': {
          backgroundColor: '#fafafa',
        },
        '& .MuiButton-startIcon': {
          color: '#f9db00',
        },
      }}
    >
      {label}
    </Button>
  );
};
